import { OrderType } from 'shared-types/Coupon'
import type { Coupon, ExternalCoupon, WeeklyAvailability, LinkedCoupon, NonExternalCoupon, Channel } from 'shared-types/Coupon'
import type { LanguageLocale } from 'utils/language'
import type { ShowSnackbarProps } from 'mobx/Infra/Infra.type'
import type { CouponUnion } from 'types/Coupons'

enum ExtraFilters {
	ALL = 'all',
	MY_COUPONS = 'myCoupons',
	KIOSK = 'kiosk',
}

export const CouponFilter = { ...ExtraFilters, ...OrderType } as const
export type Filter = ExtraFilters | OrderType

// Server-Side Stuff
export interface SearchField {
	name: string
	operator: 'eq' | 'gr' | 'lt' | 'in' | 'neq'
	value: string | string[] | number | number[]
	logicalOperator?: 'and' | 'or'
}

export interface SearchBody {
	fields: SearchField[]
	pagination?: { offset: number; limit: number }
	sort?: { fieldName: string; direction: 'ASC' | 'DESC' }
}

export interface PrbResponse {
	couponCode: string
	couponUrl: string | null
	couponData: string | null
}

export interface PrbCoupon {
	code: string
	url: string
	data: string | null
}

// Due to a TypeScript limitation, this has to be a type and not an interface
// Read more: https://www.typescriptlang.org/docs/handbook/2/everyday-types.html#differences-between-type-aliases-and-interfaces
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type GetCouponQuery = {
	storeId?: string
}

export interface Feature {
	name: 'displayInCouponWallet' | 'requireLoginWallet'
	[key: string]: string
}

export interface DateTimeObject {
	year: number
	month: number
	day: number
	hour: number
	minute: number
}

interface Exception {
	comment: Record<LanguageLocale, string>
	start: DateTimeObject
	end: DateTimeObject
	available: boolean
}

type OmittedFields = 'flags' | 'source' | 'orderTypes' | 'availability' | 'expiration' | 'channels'

export type ServerSideCoupon = Omit<CouponUnion, OmittedFields> & {
	channels?: Channel[]
	expiration: number
	features: Feature[]
	orderTypes: (OrderType | 'peakup' | 'curbside' | 'tableside' | 'dineIn' | 'takeAway')[]
	availability?: {
		weekly: WeeklyAvailability[]
		exceptions: Exception[]
	}
}

export interface CouponsDependencies {
	getInternalCoupons(): Promise<NonExternalCoupon[]>
	getExternalCoupons(): Promise<ExternalCoupon[]>
	getCoupon(code: string): Promise<Coupon | null>
	linkCoupon(code: string): Promise<LinkedCoupon>
	getPrbCode(id: string): Promise<PrbCoupon>
	getIgnoreOrderTypes(): OrderType[]
	isLocalized(): boolean
	showSnackbar: (data: ShowSnackbarProps) => void
	isUserLoggedIn(): boolean
}
